import { StorageManager } from 'src/app/common/storage-manager.class';
import {
    Form,
    FormField,
    FormSection,
    FormType,
    InputType,
    FilteredValue,
    DropdownFilterContext,
    ReactiveFormChangedField,
    ReactiveFormValue,
} from '@fitogest/reactive-form-builder/interfaces';
import { TrazabilidadEnvironmentDefinition } from '../../environment-variables';
import { TrazabilidadListsDefinition } from './list-variables';
import { TrazabilidadModel } from 'src/app/models/stock/trazabilidad.model';
import { SectorModel } from 'src/app/models/form-common/sector.model';
import { ParcelasModel } from 'src/app/models/form-common/parcelas.model';
import { VisitasVariedadMesModel } from 'src/app/models/visitas_variedad_mes/visitas_variedad_mes.model';

export class TrazabilidadFormDefinition {

    public formType!: FormType;
    public env: TrazabilidadEnvironmentDefinition = new TrazabilidadEnvironmentDefinition();
    public lists: TrazabilidadListsDefinition = new TrazabilidadListsDefinition();

    public cliente = StorageManager.getClient();
    public usuario = StorageManager.getUser();

    public model: ReactiveFormValue<TrazabilidadModel> = {} as ReactiveFormValue<TrazabilidadModel>;

    public tag: ReactiveFormChangedField<TrazabilidadModel> = '';
    public formFields!: Form<TrazabilidadModel>;
    public fieldNames: FormField<any>[] = [];
    public fieldLabels: string[] = [];

    constructor(
        type: FormType,
        _model: Record<string, string>,
        table: (string[] | string[][])[] | null
    ) {
        this.formType = +type;

        this.formFields = {
            name: 'trazabilidad',
            header: 'Trazabilidad',
            type: this.formType,
            sections: this.getFormSections(),
            table: { title: '', showIndex: false, data: table?.[0], style: table?.[1] },
            uploadButton: { isEnabled: false, label: '' },
            signed: false
        };
    }

    public getFormFields(): FormField<any>[] {
        const formFields: FormField<any>[] = [
            {
                name: 'fecha_recepcion',
                vinculatedFields: undefined,
                label: 'Fecha Recepción',
                input: { type: InputType.CALENDAR, isEditable: true },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: true,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                order: 1
            },
            {
                name: this.formType < 3 ? 'proveedor' : 'proveedor_nombre',
                vinculatedFields: undefined,
                label: 'Proveedor',
                input: {
                    type: InputType.DROPDOWN_SEARCH,
                    data: this.lists.proveedores,
                    isEditable: true,
                    dropdownConfiguration: {
                        valuePrimaryKey: 'id',
                        filter: (it: FilteredValue<any>) => it.value?.tipo === 'Proveedor'
                    }
                },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                order: 2
            },
            {
                name: 'id_finca',
                vinculatedFields: undefined,
                label: this.env.fincaName,
                input: {
                    type: InputType.DROPDOWN_SEARCH,
                    isEditable: true,
                    data: this.lists.fincas,
                    dropdownConfiguration: {
                        valuePrimaryKey: 'id'
                    }
                },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: true,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                order: 3
            },
            {
                name: this.formType < 3 ? 'id_sector' : 'sector_nombre',
                dependencies: [
                    {
                        source: 'id_finca',
                        target: this.formType < 3 ? 'id_sector' : 'sector_nombre',
                        kind: 'filter'
                    }
                ],
                vinculatedFields: undefined,
                label: this.env.sectorName,
                input: {
                    type: InputType.DROPDOWN_SEARCH,
                    data: this.lists.sectores,
                    isEditable: true,
                    dropdownConfiguration: {
                        valuePrimaryKey: 'id',
                        filter: (it: FilteredValue<SectorModel>, context?: DropdownFilterContext<SectorModel>): boolean => {
                            if (it.value == null) {
                                return true;
                            }

                            const fincaId = this.getContextValue(context, 'id_finca');

                            if (fincaId === null) {
                                return false;
                            }

                            return this.sameValue(it.value.id_finca, fincaId);
                        }
                    }
                },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: true,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                order: 4
            },
            {
                name: this.formType < 3 ? 'id_parcela' : 'parcela_nombre',
                vinculatedFields: [this.formType < 3 ? 'id_sector' : 'sector_nombre'],
                label: this.env.parcelaName,
                input: {
                    type: InputType.DROPDOWN_SEARCH,
                    data: this.lists.parcelas,
                    isEditable: true,
                    dropdownConfiguration: {
                        valuePrimaryKey: 'id',
                        filter: (it: FilteredValue<ParcelasModel>, context?: DropdownFilterContext<ParcelasModel>): boolean => {
                            if (it.value == null) {
                                return true;
                            }

                            const sectorField = this.formType < 3 ? 'id_sector' : 'sector_nombre';
                            const sectorValue = this.getContextValue(context, sectorField);

                            if (sectorValue === null) {
                                return false;
                            }

                            return this.sameValue(it.value.id_sector, sectorValue) || this.sameValue(it.value.sector, sectorValue);
                        }
                    }
                },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                order: 5
            },
            {
                name: 'cultivo',
                vinculatedFields: undefined,
                label: 'Cultivo',
                input: {
                    type: InputType.DROPDOWN_SEARCH,
                    data: this.lists.cultivos,
                    isEditable: true,
                    dropdownConfiguration: {
                        valuePrimaryKey: 'cultivo'
                    }
                },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                order: 6
            },
            {
                name: 'variedad',
                dependencies: [
                    {
                        source: 'cultivo',
                        target: 'variedad',
                        kind: 'filter'
                    }
                ],
                vinculatedFields: undefined,
                label: 'Variedad',
                input: {
                    type: InputType.DROPDOWN_SEARCH,
                    data: this.lists.variedades,
                    isEditable: true,
                    dropdownConfiguration: {
                        valuePrimaryKey: 'variedad',
                        filter: (it: FilteredValue<VisitasVariedadMesModel>, context?: DropdownFilterContext<VisitasVariedadMesModel>): boolean => {
                            if (it.value == null) {
                                return true;
                            }

                            const cultivo = this.getContextValue(context, 'cultivo');

                            if (cultivo === null) {
                                return false;
                            }

                            return this.sameValue(it.value.cultivo, cultivo);
                        }
                    }
                },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                order: 7
            },
            {
                name: 'semana_entrega_prevista',
                vinculatedFields: undefined,
                label: 'Semana Entrega Prevista',
                input: { type: InputType.TEXT, isEditable: true },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                order: 8
            },
            {
                name: 'incidencia',
                vinculatedFields: undefined,
                label: 'Incidencia',
                input: {
                    type: InputType.DROPDOWN,
                    data: this.lists.incidencias,
                    isEditable: true
                },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                order: 9
            },
            {
                name: 'cantidad',
                vinculatedFields: undefined,
                label: 'Cantidad',
                input: { type: InputType.NUMERIC, isEditable: true },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                order: 10
            },
            {
                name: 'formato',
                vinculatedFields: undefined,
                label: 'Formato',
                input: {
                    type: InputType.DROPDOWN,
                    data: this.lists.formatos,
                    isEditable: true
                },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                order: 11
            },
            {
                name: 'maceta_reciclada',
                vinculatedFields: undefined,
                label: 'Maceta Reciclada',
                input: { type: InputType.SWITCH, defaultValue: '0', isEditable: true },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                order: 12
            },
            {
                name: 'cantidad_maceta_reciclada',
                vinculatedFields: undefined,
                label: 'Cantidad Maceta Reciclada',
                input: { type: InputType.NUMERIC, isEditable: true },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                order: 13
            },
            {
                name: 'fecha_expedicion',
                vinculatedFields: undefined,
                label: 'Fecha Expedición',
                input: { type: InputType.CALENDAR, isEditable: true },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                order: 14
            },
        ];

        this.fieldNames = this.fieldNames.concat(formFields.filter(it => it.conditions.isVisible));
        this.fieldLabels = this.fieldLabels.concat(formFields.filter(it => it.conditions.isVisible).map(it => it.label));
        return formFields.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }

    private getHiddenFields(): FormField<any>[] {
        const hiddenFields: FormField<any>[] = [
            {
                name: 'id',
                vinculatedFields: undefined,
                label: '',
                input: { type: InputType.HIDDEN, defaultValue: this.model.id, isEditable: false },
                width: 2,
                conditions: {
                    isRememberedOnDuplicate: false,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                order: 1
            },
            {
                name: 'id_cliente',
                vinculatedFields: undefined,
                label: '',
                input: { type: InputType.HIDDEN, defaultValue: this.cliente?.id ?? '', isEditable: false },
                updateValue: () => this.cliente?.id ?? '',
                width: 2,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: this.env.hasClients
                },
                order: 2
            },
            {
                name: 'id_usuario',
                vinculatedFields: undefined,
                label: '',
                input: {
                    type: InputType.HIDDEN,
                    defaultValue: this.formType !== FormType.EDITION ? this.usuario?.id ?? '' : '',
                    isEditable: false
                },
                updateValue: () => this.formType !== FormType.EDITION ? this.usuario?.id ?? '' : '',
                width: 2,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                order: 3
            },
        ];

        return hiddenFields.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }

    private getContextValue<T>(context: DropdownFilterContext<T> | undefined, fieldName: string): unknown | null {
        const controlValue = context?.model.get(fieldName)?.value;

        if (this.hasValue(controlValue)) {
            return controlValue;
        }

        const modelValue = this.model[fieldName as keyof TrazabilidadModel];

        return this.hasValue(modelValue) ? modelValue : null;
    }

    private hasValue(value: unknown): boolean {
        return value !== null && value !== undefined && String(value).length > 0;
    }

    private sameValue(left: unknown, right: unknown): boolean {
        if (!this.hasValue(left) || !this.hasValue(right)) {
            return false;
        }

        return String(left) === String(right);
    }

    private getFormSections(): FormSection<any>[] {
        const sections: FormSection<any>[] = [
            {
                name: '',
                width: 12,
                isVisible: true,
                fields: this.getFormFields(),
                order: 1
            },
            {
                name: '',
                width: 6,
                isVisible: true,
                fields: this.getHiddenFields(),
                order: 2
            }
        ];
        return sections.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }
}